import { Injectable, Inject } from '@angular/core';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import * as _ from "lodash";
import * as moment from "moment";
import { TaskType } from "../../../tasks/types/task.type";
import { TaskPurposeType } from "../../../commons/types/task-purpose.type";
import { TaskModel } from "../../../tasks/models/task.model";

@Injectable({
  providedIn: 'root'
})
export class RoutesDragAndDropService {

  constructor(
    @Inject('confirmDialogService') private confirmDialogService: any,
    @Inject('planningAlertsService') private planningAlertsService: any
  ) {}

  // --------------------------------------------------------------------------------
  // 1. services-panel-list.component.ts & services-table-list.component.ts
  // --------------------------------------------------------------------------------
  public enterPredicateServices = (drag: CdkDrag<any>, drop: CdkDropList<any>): boolean => {
    return !drag.data?.routeTaskList;
  }

  public onDropServices(event: CdkDragDrop<any[]>, selectedIds?: string[]): { task: TaskModel[], routeId: string } | null {
    if (!event.isPointerOverContainer) {
      return null;
    }

    if (!event.item.data.taskType || !event.item.data.routeId) {
      return null;
    }

    const serviceId = _.get(event.item.data, 'serviceId');
    const ids = event.item.data.bundleServiceIdList || [serviceId];
    const serviceIds = _.intersectionWith(ids, selectedIds || [], _.isEqual)?.length ? selectedIds : ids;

    const routeId = event.item.data.routeId;

    if (_.intersectionWith(ids, selectedIds || [], _.isEqual)?.length && event.previousContainer && Array.isArray(event.previousContainer.data)) {
      const selectedTasks = event.previousContainer.data.filter((task: TaskModel) => {
        if (!task || task.taskType === TaskType.Internal) {
          return false;
        }
        const taskServiceId = _.get(task, 'serviceId');
        const taskIds = task.bundleServiceIdList || [taskServiceId];
        return _.intersectionWith(taskIds, serviceIds || [], _.isEqual)?.length;
      });

      if (selectedTasks.length > 0) {
        const modifiedTasks = selectedTasks.map((task: TaskModel) => {
          if (task.bundleServiceIdList) {
            return {
              ...task,
              bundleServiceIdList: _.intersectionWith(task.bundleServiceIdList, serviceIds || [], _.isEqual)
            };
          }
          return task;
        });
        return { task: modifiedTasks, routeId };
      }
    }

    const singleTask = event.item.data as TaskModel;
    if (singleTask.bundleServiceIdList) {
      const modifiedSingleTask = {
        ...singleTask,
        bundleServiceIdList: _.intersectionWith(singleTask.bundleServiceIdList, serviceIds || [], _.isEqual)
      };
      return { task: [modifiedSingleTask], routeId: singleTask.routeId };
    }

    return { task: [singleTask], routeId: singleTask.routeId };
  }

  // --------------------------------------------------------------------------------
  // 2. resources-panel-list.component.ts & resources-table-list.component.ts
  //    & routes-panel-resource-view.component.ts & routes-table-resource-view.component.ts
  // --------------------------------------------------------------------------------
  public onDropResources(
    resource: any,
    event: CdkDragDrop<any>,
    selectedServiceIds: string[]
  ): Promise<{ type: 'update' | 'add' | 'resequence', data: any } | null> {
    if (!event.isPointerOverContainer) {
      return Promise.resolve(null);
    }

    if (!!resource.resourceAllocation?.disableRetrips && (resource.scheduleExtraData?.routeIds?.length > 0)) {
      this.planningAlertsService.resourceNotAllowingRetripsInfo();
      return Promise.resolve(null);
    }

    const isRoute: boolean = !!_.get(event.item.data, 'routeTaskList');
    const isService = !!_.get(event.item.data, 'serviceType');

    if (isRoute) {
      if (event.previousContainer === event.container) {
        moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        const orderedRouteIds: string[] = event.container.data.map((r: any) => {
          return r.id;
        });
        const routeGroupId = event.item.data.routeGroupId;
        return Promise.resolve({
          type: 'resequence',
          data: { routeGroupId: routeGroupId, orderedRouteIds: orderedRouteIds }
        });
      } else {
        const draggedRoute = event.item.data;
        if (Array.isArray(event.previousContainer.data)) {
          const sourceIndex = event.previousContainer.data.indexOf(draggedRoute);
          if (sourceIndex > -1) {
            event.previousContainer.data.splice(sourceIndex, 1);
          }
        }
        if (Array.isArray(event.container.data)) {
          event.container.data.splice(event.currentIndex, 0, draggedRoute);
        }
        return Promise.resolve({
          type: 'update',
          data: { routeId: draggedRoute.id, resource: resource }
        });
      }
    }

    const isTask = !!_.get(event.item.data, 'taskType');
    const serviceId = isService ? _.get(event.item.data, 'id') : _.get(event.item.data, 'serviceId');
    const eventIds = event.item.data.bundleServiceIdList || [serviceId];
    const serviceIds = _.intersectionWith(eventIds, selectedServiceIds, _.isEqual)?.length ?
      selectedServiceIds :
      eventIds;

    if ((!serviceId && !serviceIds) || (!isService && !isTask)) {
      return Promise.resolve(null);
    }

    if (serviceIds.length >= 10) {
      return this.confirmDialogService.confirmWithInfo('Confirm', 'MOVE_X_JOBS_CONFIRM', '', {x: serviceIds.length}, event)
        .then(() => {
          if (isService || isTask) {
            return {
              type: 'add',
              data: { resource: resource, serviceIds: serviceIds }
            };
          }
          return null;
        })
        .catch(() => null);
    } else {
      if (isService || isTask) {
        return this.confirmDialogService.confirm('Confirm', 'A new route will be created by this action. Would you like to continue?', event)
          .then(() => ({
            type: 'add' as const,
            data: { resource: resource, serviceIds: serviceIds }
          }))
          .catch(() => null);
      }
      return Promise.resolve(null);
    }
  }

  // --------------------------------------------------------------------------------
  // 3. routes-table-list.component.ts & routes-table-route-view.component.ts / routes-panel-route-view.component.ts
  // --------------------------------------------------------------------------------
  public onDropRoute(
    route: any,
    event: CdkDragDrop<any>,
    selectedServiceIds: string[],
    expandedRoutes?: any[],
    tasks?: TaskModel[]
  ): Promise<{ type: 'move' | 'add', data: any } | null> {
    if (!event.isPointerOverContainer) {
      return Promise.resolve(null);
    }

    if (route?.isBlockedByUser) {
      this.planningAlertsService.routeIsLocked();
      return Promise.resolve(null);
    }

    if (!!event.item.data?.serviceType) {
      return this._dropService(route, event, tasks, selectedServiceIds);
    } else {
      return this._dropTask(route, event, selectedServiceIds);
    }
  }

  private _dropService(
    route: any,
    event: CdkDragDrop<any>,
    tasks: TaskModel[],
    selectedServiceIds?: string[]
  ): Promise<{ type: 'move' | 'add', data: any } | null> {
    const serviceId = _.get(event.item.data, 'id');
    const bundleIds = event.item.data.bundleServiceIdList || [serviceId];
    const isMultiSelectActive = selectedServiceIds?.length > 1 && _.intersectionWith(bundleIds, selectedServiceIds, _.isEqual)?.length > 0;
    const payloadServiceIds = isMultiSelectActive ? selectedServiceIds : bundleIds;
    let itemsToDrop = [event.item.data];

    if (isMultiSelectActive && event.previousContainer?.data && Array.isArray(event.previousContainer.data)) {
      const filteredItems = event.previousContainer.data.filter((item: any) => {
        if (!item) return false;
        return _.includes(selectedServiceIds, item.id);
      });

      if (filteredItems.length > 0) {
        itemsToDrop = filteredItems;
      }
    }

    const task: TaskModel = event.item.data.taskList?.[0];
    const serviceNotBefore: string = event.item.data.notBefore;
    const serviceRouteId: string = event.item.data.routeId;
    const currentRouteSkills = route.requiredSkills || [];

    const taskSkills = isMultiSelectActive
      ? _.uniq(_.flatMap(itemsToDrop, (s) => _.flatMap(s.taskList || [], (t: any) => t.serviceInfo?.requiredSkills || [])))
      : (task?.serviceInfo?.requiredSkills || []);

    if (currentRouteSkills && !!_.difference(taskSkills, currentRouteSkills).length) {
      this.planningAlertsService.noMatchingSkillsInfo();
      return Promise.resolve(null);
    }

    // Already In Route
    const alreadyInRoute = isMultiSelectActive
      ? _.some(itemsToDrop, (s) => s.routeId === route.id)
      : serviceRouteId === route.id;

    if (alreadyInRoute) {
      this.planningAlertsService.alreadyInRouteInfo();
      return Promise.resolve(null);
    }

    const fromRouteId = isMultiSelectActive
      ? _.get(itemsToDrop[0], 'taskList[0].routeId') || _.get(itemsToDrop[0], 'taskList[0].data[0].routeId') || _.get(itemsToDrop[0], 'routeId')
      : (_.get(task, 'data[0].routeId') || event.item.data.routeId);
    const toRouteId = route.id;

    if ((event.previousContainer === event.container) && (event.currentIndex === event.previousIndex)) {
      return Promise.resolve(null);
    }

    const tasksCopy = [...tasks];
    const serviceIndex = event.currentIndex;
    const tasksToInsert = isMultiSelectActive
      ? _.flatMap(itemsToDrop, (s) => s.taskList || [])
      : (task ? [task] : []);
    tasksCopy.splice(serviceIndex, 0, ...tasksToInsert);
    const toOrderedIds = this._getStopsOrderIds(tasksCopy);

    const isInRouteTimeRange = moment(moment(serviceNotBefore).millisecond(1)).isBetween(
      moment(route.notBefore).startOf('day'),
      moment(route.notAfter).endOf('day')
    );

    const payload = {
      serviceId: isMultiSelectActive ? payloadServiceIds : (event.item.data.bundleServiceIdList || event.item.data.id),
      fromRouteId: fromRouteId,
      toRouteId: toRouteId,
      fromOrderedIds: _.map(event.previousContainer.data, 'id'),
      toOrderedIds: toOrderedIds,
      isBetweenDays: !isInRouteTimeRange,
      toDate: !isInRouteTimeRange ? route.notBefore : undefined
    };

    return Promise.resolve({
      type: fromRouteId ? 'move' : 'add',
      data: payload
    });
  }

  private _dropTask(
    route: any,
    event: CdkDragDrop<any>,
    selectedServiceIds: string[]
  ): Promise<{ type: 'move' | 'add', data: any } | null> {
    const task: TaskModel = event.item.data;
    const isBundle = task.taskType === TaskType.BundledStopover;
    const currentRouteSkills = route.requiredSkills || [];
    const taskSkills = isBundle
      ? _.uniq(_.flatMap(task.bundledTaskList, 'serviceInfo.requiredSkills'))
      : (task.serviceInfo.requiredSkills || []);

    if (currentRouteSkills && !!_.difference(taskSkills, currentRouteSkills).length) {
      this.planningAlertsService.noMatchingSkillsInfo();
      return Promise.resolve(null);
    }

    const fromRouteId = task.routeId || _.get(event.previousContainer, 'data[0].routeId');
    const payloadServiceIds = task.bundleServiceIdList || [task.serviceId];
    const isMultiSelectActive = selectedServiceIds?.length > 1 && _.intersectionWith(payloadServiceIds, selectedServiceIds, _.isEqual)?.length > 0;
    const targetServiceIds = isMultiSelectActive ? selectedServiceIds : payloadServiceIds;

    if (event.previousContainer === event.container) {
      const allTasksToMove = [task];

      const tasksToRemoveIds = _.map(allTasksToMove, 'id');
      _.remove(event.container.data, (t: any) => tasksToRemoveIds.includes(t.id));
      event.container.data.splice(event.currentIndex, 0, ...allTasksToMove);
    } else {
      const sourceRouteTasks = event.previousContainer.data || [];
      const allTasksToMove = sourceRouteTasks.filter((t: any) => {
        const serviceIdsOfTask = t.bundleServiceIdList || [t.serviceId];
        return _.intersection(serviceIdsOfTask, targetServiceIds).length > 0;
      });

      const tasksToRemoveIds = _.map(allTasksToMove, 'id');
      _.remove(event.previousContainer.data, (t: any) => tasksToRemoveIds.includes(t.id));
      event.container.data.splice(event.currentIndex, 0, ...allTasksToMove);
    }

    const toRouteId = route.id;

    if ((event.previousContainer === event.container) && (event.currentIndex === event.previousIndex)) {
      return Promise.resolve(null);
    }

    const payload = {
      serviceId: isMultiSelectActive ? targetServiceIds : (task.bundleServiceIdList || task.serviceId),
      fromRouteId: fromRouteId,
      toRouteId: toRouteId,
      fromOrderedIds: _.map(event.previousContainer.data, 'id'),
      toOrderedIds: _.map(event.container.data, 'id')
    };

    return Promise.resolve({
      type: fromRouteId ? 'move' : 'add',
      data: payload
    });
  }

  public onDropRouteList(
    route: any,
    event: CdkDragDrop<any>,
    selectedServiceIds: string[],
    expandedRoutes?: any[]
  ): Promise<{ type: 'move' | 'add', data: any } | null> {
    if (!event.isPointerOverContainer) {
      return Promise.resolve(null);
    }

    const isService = !!_.get(event.item.data, 'serviceType');
    const isTask = !!_.get(event.item.data, 'taskType');

    if (!isService && !isTask) {
      return Promise.resolve(null);
    }

    const task: TaskModel = isTask ? event.item.data : null;
    const serviceId = isService ? _.get(event.item.data, 'id') : _.get(event.item.data, 'serviceId');
    const bundleIds = event.item.data.bundleServiceIdList || [serviceId];
    const isMultiSelectActive = selectedServiceIds?.length > 1 && _.intersectionWith(bundleIds, selectedServiceIds, _.isEqual)?.length > 0;
    const payloadServiceIds = isMultiSelectActive ? selectedServiceIds : bundleIds;
    let itemsToDrop = [event.item.data];

    if (isMultiSelectActive && event.previousContainer?.data && Array.isArray(event.previousContainer.data)) {
      const filteredItems = event.previousContainer.data.filter((item: any) => {
        if (!item) return false;
        if (isService) return _.includes(selectedServiceIds, item.id);
        return _.includes(selectedServiceIds, item.serviceId) || _.intersectionWith(item.bundleServiceIdList || [], selectedServiceIds, _.isEqual)?.length;
      });

      if (filteredItems.length > 0) {
        itemsToDrop = filteredItems;
      }
    }

    if (route?.isBlockedByUser) {
      this.planningAlertsService.routeIsLocked();
      return Promise.resolve(null);
    }

    const fromRouteId = isService
      ? _.get(itemsToDrop[0], 'taskList[0].routeId') || _.get(itemsToDrop[0], 'taskList[0].data[0].routeId') || _.get(itemsToDrop[0], 'routeId')
      : (task?.routeId || _.get(event.previousContainer, 'data[0].routeId'));

    const fromRoute = fromRouteId ? _.find(expandedRoutes || [], { id: fromRouteId }) : null;

    if (fromRoute?.isBlockedByUser) {
      this.planningAlertsService.routeIsLocked();
      return Promise.resolve(null);
    }

    const currentRouteSkills = route.requiredSkills || [];
    const taskToValidate: any = isService ? (itemsToDrop[0]?.taskList ? itemsToDrop[0].taskList[0] : itemsToDrop[0]) : itemsToDrop[0];
    const isBundle = !isService && taskToValidate?.taskType === TaskType.BundledStopover;
    const taskSkills = isBundle
      ? _.uniq(_.flatMap(taskToValidate?.bundledTaskList, 'serviceInfo.requiredSkills'))
      : (taskToValidate?.serviceInfo?.requiredSkills || []);

    if (currentRouteSkills && !!_.difference(taskSkills, currentRouteSkills).length) {
      this.planningAlertsService.noMatchingSkillsInfo();
      return Promise.resolve(null);
    }

    if (isService && event.item.data.routeId === route.id) {
      this.planningAlertsService.alreadyInRouteInfo();
      return Promise.resolve(null);
    }

    let stopsToAdd: string[] = [];
    if (isService) {
      stopsToAdd = _.flatMap(itemsToDrop, (s) => _.flatMap(s?.taskList || [], 'id'));
    } else {
      const sourceRouteTasks = event.previousContainer.data || [];
      const tasksToMove = sourceRouteTasks.filter((t: any) => {
        const serviceIdsOfTask = t.bundleServiceIdList || [t.serviceId];
        return _.intersection(serviceIdsOfTask, payloadServiceIds).length > 0;
      });
      stopsToAdd = _.map(tasksToMove, 'id');

      // Remove from source container on the frontend
      const tasksToRemoveIds = _.map(tasksToMove, 'id');
      _.remove(event.previousContainer.data, (t: any) => tasksToRemoveIds.includes(t.id));
    }

    const existingStops = _.chain(route.routeTaskList || [])
      .filter((t: any) => t.taskType === TaskType.Stopover || (t.taskType === TaskType.Internal && t.taskSpec?.purpose === TaskPurposeType.Break))
      .map('id')
      .value();

    const toOrderedIds = _.uniq([...existingStops, ...stopsToAdd]);
    let fromOrderedIds: string[] = [];

    if (fromRouteId) {
      if (isTask && event.previousContainer?.data) {
        fromOrderedIds = _.map(event.previousContainer.data, 'id');
      } else if (fromRoute) {
        const previousStops = _.chain(fromRoute.routeTaskList || [])
          .filter((t: any) => t.taskType === TaskType.Stopover || (t.taskType === TaskType.Internal && t.taskSpec?.purpose === TaskPurposeType.Break))
          .map('id')
          .value();
        fromOrderedIds = _.difference(previousStops, stopsToAdd);
      }
    }

    const payload: any = {
      serviceId: isMultiSelectActive ? payloadServiceIds : (isService ? payloadServiceIds : (task?.bundleServiceIdList || task?.serviceId)),
      fromRouteId,
      toRouteId: route.id,
      fromOrderedIds,
      toOrderedIds
    };

    if (isService) {
      const serviceNotBefore = event.item.data.notBefore;
      const isInRouteTimeRange = moment(serviceNotBefore).millisecond(1).isBetween(
        moment(route.notBefore).startOf('day'),
        moment(route.notAfter).endOf('day')
      );

      payload.isBetweenDays = !isInRouteTimeRange;

      if (!isInRouteTimeRange) {
        payload.toDate = route.notBefore;
      }
    }

    return Promise.resolve({
      type: fromRouteId ? 'move' : 'add',
      data: payload
    });
  }

  private _getStopsOrderIds(tasks: any[]) {
    return _.chain(tasks)
      .filter((t: any) => {
        return t.taskType !== TaskType.Internal;
      })
      .map('id')
      .flatten()
      .value();
  }

}
